import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import DateRangeFilter, { rangeFromPreset } from "../../../components/dateRangeFilter/DateRangeFilter";
import CustomTable from "../../../components/customTable";
import useAuth from "../../../hooks/useAuth";
import { useUrlState, useUrlStateBatch } from "../../../hooks/useUrlState";
import {
  getAdminPackages,
  getPackagesByStatusCount,
} from "../../../api/packages";
import { getPlatformLogisticsCompanies } from "../../../api/settings";
import { roleRoutes } from "../../../utils/roles";
import { packageStatus, packageStatusStyles } from "../../../utils/packageStatus";
import { showToast } from "../../../utils/showToast";
import { apiBodyMessage } from "../../../utils/apiErrorMessage";
import { deliveredFromResume } from "../../../utils/packagesResume";
import { ErrorState } from "../../../components/dashboard/shared/DashboardShared";
import { SummaryCardsSkeleton } from "../../../components/ui/SummaryCardsSkeleton";
import { TableSkeleton } from "../../../components/ui/TableSkeleton";
import EvidenceView from "../../../components/evidenceView";
import moment from "moment";
import Spinner from "../../../components/spinner/spinner";
import PendingAddressChip from "../../../components/addressPicker/PendingAddressChip";

// Identificadores estables de las KPI-cards-como-filtro (para ?card= en la URL).
// El status es estático; el título/valor se resuelven en render (i18n + counts).
const CARD_DEFS = [
  { key: "pending", status: [0] },
  { key: "picked", status: [1] },
  { key: "warehouse", status: [2] },
  { key: "route", status: [3, 30, 31, 32] },
  { key: "delivered", status: [4] },
  { key: "failed", status: [6, 7] },
  { key: "returns", status: [21, 22] },
];

// Reconstruye el objeto `filters` desde los params de la URL (card/status/type)
// + el rango de fechas vigente. Sin card ni status/type ⇒ {} (idéntico al
// default actual, que deja que loadData use showResume + updatedAtSince).
function buildFiltersFromUrl(cardParam, statusParam, typeParam, dateStart, dateEnd) {
  if (cardParam) {
    const def = CARD_DEFS.find((c) => c.key === cardParam);
    if (def) return { status: def.status, dateStart, dateEnd };
  }
  const f = {};
  if (statusParam && statusParam.length) {
    f.status = statusParam.map(Number);
    f.dateStart = dateStart;
    f.dateEnd = dateEnd;
  }
  if (typeParam) {
    f.type = typeParam;
    f.dateStart = dateStart;
    f.dateEnd = dateEnd;
  }
  return f;
}

export default function Resume({ update, resetUpdate }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [inStorage, setInStorage] = useState(0);
  const [pending, setPending] = useState(0);
  const [picked, setPicked] = useState(0);
  const [inWarehouse, setInWarehouse] = useState(0);
  const [inRoute, setInRoute] = useState(0);
  const [delivered, setDelivered] = useState(0);
  const [pendingReturns, setPendingReturns] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [updateData, setUpdateData] = useState(false);
  const [hasStartedLoading, setHasStartedLoading] = useState(false);
  const [isRequestComplete, setIsRequestComplete] = useState(false);
  // Backend text when the load fails: without it a failed request looked the
  // same as "no results" (empty table).
  const [loadError, setLoadError] = useState(null);
  const [viewEvidence, setViewEvidence] = useState(false);
  const [packageEvidence, setPackageEvidence] = useState({});
  // URL-state: filtros compartibles + back/forward, sembrados de forma SÍNCRONA
  // ANTES del primer fetch. Sin params en la URL ⇒ cada default == el de hoy ⇒
  // comportamiento idéntico. NO cambia la firma del fetch (los params se siguen
  // derivando de `filters`/`query`/`showResume` igual que antes).
  const [qParam, setQParam] = useUrlState("q", "");
  // preset + rango 'custom' (?from/?to en YMD junto a ?preset=custom) se LEEN con
  // useUrlState pero se ESCRIBEN JUNTOS con useUrlStateBatch: react-router no
  // encadena dos setSearchParams síncronos, así que setear preset/from/to con
  // setters separados en el mismo tick hacía que las navegaciones se pisaran
  // (solo sobrevivía la última, p.ej. `to`, dejando `from`/`preset` en su default
  // y rompiendo el filtro de rango). Un único setDateRange los aplica atómicamente.
  const [datePreset] = useUrlState("preset", "7d");
  const [fromParam] = useUrlState("from", "");
  const [toParam] = useUrlState("to", "");
  const setDateRange = useUrlStateBatch({
    preset: { type: "string", defaultValue: "7d" },
    from: { type: "string", defaultValue: "" },
    to: { type: "string", defaultValue: "" },
  });
  const [statusParam, setStatusParam] = useUrlState("status", [], { type: "csv" });
  const [typeParam, setTypeParam] = useUrlState("type", "");
  const [cardParam, setCardParam] = useUrlState("card", "");

  const [query, setQuery] = useState(() => ({ type: "", query: qParam }));
  const [filters, setFilters] = useState(() => {
    const [s, e] =
      datePreset === "custom" && fromParam && toParam
        ? [moment(fromParam, "YYYY-MM-DD"), moment(toParam, "YYYY-MM-DD")]
        : rangeFromPreset(datePreset);
    return buildFiltersFromUrl(
      cardParam,
      statusParam,
      typeParam,
      s.format("YYYY-MM-DD"),
      e.format("YYYY-MM-DD")
    );
  });
  const [showResume, setShowResume] = useState(() => {
    const [s, e] =
      datePreset === "custom" && fromParam && toParam
        ? [moment(fromParam, "YYYY-MM-DD"), moment(toParam, "YYYY-MM-DD")]
        : rangeFromPreset(datePreset);
    return { startDate: s.format("MM/DD/YYYY"), endDate: e.format("MM/DD/YYYY") };
  });
  // Ref viva del rango actual: la usa el efecto de back/forward de `preset` para
  // decidir si hay que re-sembrar (sin meter showResume en sus deps y así no
  // pisar filtros de columna al cambiar el preset).
  const showResumeRef = useRef(showResume);
  showResumeRef.current = showResume;
  // true cuando el usuario quitó el filtro de fecha: el scope pasa a "todo el
  // histórico" (sin acotar por fecha). Lo activa la "X" del rango
  // (DateRangeFilter.onClear).
  const [dateFilterCleared, setDateFilterCleared] = useState(false);
  // Ampliación incremental del estado vacío: cada click suma 30 días hacia
  // atrás al inicio del rango. Guardamos el número de ampliaciones aplicadas
  // (0 = sin ampliar) y un tope para no buscar indefinidamente.
  const MAX_DATE_EXPANSIONS = 6; // ≈ 180 días (~6 meses)
  const DATE_EXPANSION_DAYS = 30;
  const [dateExpansions, setDateExpansions] = useState(0);
  // Rango concreto (YYYY-MM-DD) cuando se está ampliando, para reflejarlo en el
  // resumen del DateRangeFilter (modo "custom").
  const [expandedRange, setExpandedRange] = useState(null);
  const [updateCards, setUpdateCards] = useState(true);
  const [selectedCard, setSelectedCard] = useState(() => {
    if (!cardParam) return null;
    const def = CARD_DEFS.find((c) => c.key === cardParam);
    return def ? { key: def.key, filter: { status: def.status } } : null;
  });
  const observerTarget = useRef(null);
  const observerRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  // Alto real de la fila de KPI-cards sticky: se mide y se publica como CSS var
  // (`--orders-cards-sticky-h`) para que la barra de búsqueda/filtros se ancle
  // justo debajo durante el scroll (stacking sin solaparse). Se recalcula ante
  // cambios de breakpoint vía ResizeObserver.
  const cardsRef = useRef(null);
  const [cardsStickyH, setCardsStickyH] = useState(0);
  // Inicio del rango ANTES de la primera ampliación incremental (MM/DD/YYYY).
  // Se fija al primer click de "Ampliar búsqueda" y sirve de ancla para
  // calcular cada +30 días sin acumular errores de redondeo.
  const expandBaseStartRef = useRef(null);

  const { height } = window.visualViewport || {};
  const totalDocsPerPage = Math.floor(((height || window.innerHeight || 800) * 0.53) / 55 - 2);
  const docsPerPage = Math.max(totalDocsPerPage || 6, 20); // Aumentar a 20 para scroll infinito más fluido

  // ── Scope de plataforma (superadmin del sistema) ─────────────────────────
  // Es el único rol sin operador propio: ve los pedidos de TODOS. Su "operador
  // efectivo" es el que elija en el selector, y vacío significa todos.
  const isPlatform = user?.role === roleRoutes.systemSuperadmin;
  const [operadorParam, setOperadorParam] = useUrlState("operador", "");
  const [operadores, setOperadores] = useState([]);
  const logisticCompanyId = isPlatform
    ? operadorParam || null
    : user?.logisticCompanyId || null;

  // Catálogo de operadores para el selector. Solo el superadmin del sistema
  // puede pedirlo (el backend responde 403 al resto), así que ni se intenta.
  useEffect(() => {
    if (!isPlatform) return;
    let vivo = true;
    getPlatformLogisticsCompanies()
      .then((res) => {
        if (vivo) setOperadores(res?.data ?? []);
      })
      .catch((error) => {
        // El selector es una ayuda, no un requisito: si falla, la vista sigue
        // mostrando todos los operadores.
        console.error("Error al obtener el catálogo de operadores:", error);
      });
    return () => {
      vivo = false;
    };
  }, [isPlatform]);

  // Función para cargar datos (primera carga o recarga)
  const loadData = useCallback(async (page = 1, reset = false, isInitialLoad = false) => {
    if (!user?.id) return;
    
    if (reset) {
      // Establecer loadingTable y resetear datos de forma síncrona
      setLoadingTable(true);
      // Solo establecer loading=true en la carga inicial, no en búsquedas/filtros
      if (isInitialLoad) {
        setLoading(true);
      }
      setLoadError(null);
      setIsRequestComplete(false); // Marcar que la petición no ha terminado
      setData([]);
      setTableData([]); // Resetear tableData inmediatamente para evitar mostrar datos antiguos
      setCurrentPage(1);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }

    setUpdateData(false);
    resetUpdate(false);

    const dateStartFromFilters = filters?.dateStart
      ? moment(filters.dateStart, "YYYY-MM-DD").format("MM/DD/YYYY")
      : undefined;

    // endDateCreation is already inclusive (end of day) in the backend: no +1 day.
    const dateEndFromFilters = filters?.dateEnd
      ? moment(filters.dateEnd, "YYYY-MM-DD").format("MM/DD/YYYY")
      : undefined;

    const hasDateFilters = Boolean(dateStartFromFilters || dateEndFromFilters);
    const isBlankSearch = query.query === "";

    const startDateParam = !hasDateFilters && isBlankSearch
      ? showResume.startDate
      : dateStartFromFilters || undefined;

    const endDateParam = !hasDateFilters && isBlankSearch
      ? showResume.endDate
      : dateEndFromFilters || undefined;

    const updatedAtSinceParam = (!hasDateFilters && isBlankSearch)
      ? showResume.startDate
      : undefined;

    try {
      const baseArgs = {
        limit: docsPerPage,
        page: page,
        sortType: "desc",
        orderField: filters?.orderBy || "updatedAt",
        coms: filters?.coms,
        zipCode: filters?.zipCode,
        status: filters?.status,
        type: filters?.type,
        startDate: startDateParam,
        endDate: endDateParam,
        assignTo: filters?.deliveryId,
        client: filters?.clientId,
        updatedAtSince: updatedAtSinceParam,
        meliStatusMismatch: filters?.meliStatusMismatch || undefined,
        // Solo viaja en el scope de plataforma: para el resto de los roles el
        // operador sale del token y el backend descarta este param.
        logisticCompanyId: isPlatform ? logisticCompanyId || undefined : undefined,
      };

      // Búsqueda unificada: un solo request con parámetro `q` que matchea
      // followingNumber + order_id + shipping_address.address_line con
      // coincidencia parcial en el backend. Reemplaza el patrón anterior de
      // 3 requests paralelos + dedupe manual + paginación rota.
      //
      // Cuando hay búsqueda activa SIN filtros de fecha propios, restringimos
      // al rango visible (showResume) para no buscar sobre todo el histórico.
      // Cuando no hay búsqueda, las fechas ya están en baseArgs (startDateParam
      // / endDateParam fueron calculados arriba con la misma lógica).
      const requestArgs = isBlankSearch
        ? { ...baseArgs, query }
        : {
            ...baseArgs,
            startDate: hasDateFilters ? dateStartFromFilters : showResume.startDate,
            endDate: hasDateFilters ? dateEndFromFilters : showResume.endDate,
            updatedAtSince: hasDateFilters ? undefined : showResume.startDate,
            q: query.query,
          };

      const response = await getAdminPackages(requestArgs);

      // `getAdminPackages` never throws: on failure it resolves `{error: body}`.
      // A transport failure resolves `{error: undefined}` too, so the check is
      // "has the key", not "is the value defined" — a successful axios response
      // never carries an `error` key. Without this guard the error branch below
      // was unreachable and a failed request rendered as an empty (i.e. "no
      // results") table.
      if (response && Object.prototype.hasOwnProperty.call(response, "error")) {
        const message =
          apiBodyMessage(response.error) ?? t("No se pudieron cargar los pedidos");
        showToast(message, false);
        if (reset) {
          setLoadError(message);
          setData([]);
          setTotalDocs(0);
          setIsRequestComplete(true);
        } else {
          // Stop infinite scroll from retrying a failing page forever.
          setHasMore(false);
        }
        return;
      }

      if (response?.data?.docs) {
        const result = response.data.docs;
        setTotalDocs(response.data.totalDocs);

        if (reset) {
          setData(result);
          setIsRequestComplete(true); // Marcar que la petición terminó
        } else {
          setData(prevData => [...prevData, ...result]);
        }

        // Verificar si hay más páginas
        const totalPages = Math.ceil((response.data.totalDocs || 0) / docsPerPage);
        const hasMorePages = page < totalPages;
        setHasMore(hasMorePages);
      } else {
        if (reset) {
          setData([]);
          setTotalDocs(0);
          setIsRequestComplete(true); // Marcar que la petición terminó (aunque no haya datos)
        }
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      showToast(t("Error de servidor"), false);
      if (reset) {
        setData([]);
        setTotalDocs(0);
        setIsRequestComplete(true); // Marcar que la petición terminó (con error)
        // Establecer loadingTable en false cuando hay error
        setLoadingTable(false);
        // Solo establecer loading=false si es la carga inicial
        if (isInitialLoad) {
          setLoading(false);
        }
      }
    } finally {
      if (reset) {
        // No establecer loading en false aquí
        // Se establecerá en false cuando los datos se procesen en el useEffect
        // Esto asegura que el loading se muestre hasta que los datos estén listos
      } else {
        setLoadingMore(false);
      }
    }
  }, [user?.id, docsPerPage, query, filters, showResume, resetUpdate, isPlatform, logisticCompanyId, t]);

  // useEffect para carga inicial o cuando cambian filtros/búsqueda
  useEffect(() => {
    if (user?.id) {
      // Determinar si es la carga inicial (solo la primera vez que se carga user?.id)
      const isInitialLoad = isInitialLoadRef.current;
      
      if (isInitialLoad) {
        // Carga inicial: establecer loading y loadingTable
        setLoading(true);
        setLoadingTable(true);
        setHasStartedLoading(true); // Marcar que la carga ha comenzado
        isInitialLoadRef.current = false; // Marcar que ya no es la carga inicial
      } else {
        // Búsqueda/filtro: solo establecer loadingTable (no loading para evitar recargar toda la vista)
        setLoadingTable(true);
      }
      
      setTableData([]); // Resetear tableData inmediatamente
      loadData(1, true, isInitialLoad);
    }
    // No establecer loading/loadingTable en false si user?.id no está disponible
    // Esto permite que el loading se muestre mientras se carga el usuario
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.id,
    updateData,
    update,
    query,
    filters,
    showResume,
    // Cambiar de operador en el selector re-dispara la carga desde la página 1.
    logisticCompanyId,
  ]);

  // Función para cargar más datos (scroll infinito)
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || !user?.id) {
      return;
    }
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    loadData(nextPage, false);
  }, [loadingMore, hasMore, currentPage, user?.id, loadData]);

  // Observer para detectar cuando el usuario llega al final
  useEffect(() => {
    // Limpiar observer anterior si existe
    if (observerRef.current && observerTarget.current) {
      observerRef.current.unobserve(observerTarget.current);
      observerRef.current = null;
    }

    // Solo configurar observer si hay más datos, no está cargando más, no está cargando la tabla, y hay datos cargados
    if (!hasMore || loadingMore || loadingTable || !data.length || !user?.id) return;

    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // Verificar que el elemento está intersectando y las condiciones se cumplen
        if (entry.isIntersecting && hasMore && !loadingMore && !loadingTable && user?.id) {
          loadMore();
        }
      },
      {
        root: null, // Usar viewport de la ventana
        rootMargin: '300px', // Empezar a cargar cuando está a 300px del final
        threshold: 0.01
      }
    );

    observer.observe(currentTarget);
    observerRef.current = observer;

    return () => {
      if (observerRef.current && currentTarget) {
        observerRef.current.unobserve(currentTarget);
        observerRef.current = null;
      }
    };
  }, [hasMore, loadingMore, loadingTable, user?.id, loadMore, data.length]);

  // Llamada a getPackagesByStatusCount
  useEffect(() => {
    // El superadmin del sistema no tiene operador propio: sin operador elegido
    // las cards cuentan TODOS (el backend solo le permite eso a él). El resto
    // de los roles sigue necesitando su operador para no consultar en vacío.
    if (updateCards && (logisticCompanyId || isPlatform)) {
      getPackagesByStatusCount({
        logisticCompanyId,
        startDate: moment(showResume.startDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
        endDate: moment(showResume.endDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
      })
        .then((data) => {
          setPicked(data.status1 || 0);
          setInWarehouse(data.status2 || 0);
          setInRoute(data.status3_30);
          setDelivered(deliveredFromResume(data));
          setInStorage(data.status6_7);
          setPendingReturns(data.status21_22 || 0);
        })
        .catch((error) => {
          console.error("Error en getPackagesByStatusCount:", error);
        })
        .finally(() => {
          setUpdateCards(false);
        });

      // El endpoint de resumen no expone los pendientes (status 0). Los contamos
      // aparte con un request liviano (limit 1 → totalDocs), mismo rango de fecha
      // → coincide con lo que muestra la card al clickearla.
      getAdminPackages({
        limit: 1,
        page: 1,
        status: [0],
        startDate: showResume.startDate,
        endDate: showResume.endDate,
        orderField: "updatedAt",
        sortType: "desc",
        logisticCompanyId: isPlatform ? logisticCompanyId || undefined : undefined,
      })
        .then((r) => setPending(r?.data?.totalDocs || 0))
        .catch(() => setPending(0));
    }
  }, [logisticCompanyId, isPlatform, showResume, updateCards]);

  // Back/forward — re-siembra la búsqueda aplicada desde ?q=. En el primer
  // render es no-op (el estado ya se sembró igual), así que no dispara un fetch
  // extra; solo reacciona cuando el historial cambia el valor.
  useEffect(() => {
    setQuery((prev) => (prev.query === qParam ? prev : { type: prev.type, query: qParam }));
  }, [qParam]);

  // Back/forward — re-siembra el rango desde la URL: ?preset=custom se reconstruye
  // desde ?from/?to; cualquier otro preset deriva de rangeFromPreset. Guard por
  // igualdad para que el primer render y los cambios propios (handleRangeChange,
  // que ya setea showResume) NO provoquen un fetch duplicado.
  useEffect(() => {
    let s;
    let e;
    if (datePreset === "custom") {
      if (!fromParam || !toParam) return;
      s = moment(fromParam, "YYYY-MM-DD");
      e = moment(toParam, "YYYY-MM-DD");
    } else {
      [s, e] = rangeFromPreset(datePreset);
    }
    const startDate = s.format("MM/DD/YYYY");
    const endDate = e.format("MM/DD/YYYY");
    const cur = showResumeRef.current;
    if (cur.startDate === startDate && cur.endDate === endDate) return;
    setShowResume({ startDate, endDate });
    setUpdateCards(true);
    setCurrentPage(1);
    // Si hay un filtro de estado activo (filters con fechas), seguimos su rango.
    setFilters((prev) =>
      prev && (prev.dateStart || prev.dateEnd)
        ? { ...prev, dateStart: s.format("YYYY-MM-DD"), dateEnd: e.format("YYYY-MM-DD") }
        : prev
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datePreset, fromParam, toParam]);

  // useEffect para resetear tableData cuando loadingTable se establece en true
  useEffect(() => {
    if (loadingTable) {
      setTableData([]);
    }
  }, [loadingTable]);

  // Mide el alto de la fila de KPI-cards para anclar la barra sticky de
  // búsqueda/filtros justo debajo. Se re-vincula al salir del estado de carga
  // (cuando las cards reales se montan) y reacciona a cambios de tamaño.
  useEffect(() => {
    const el = cardsRef.current;
    if (!el) return;
    const update = () => setCardsStickyH(el.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  // Parseo de datos para la tabla
  useEffect(() => {
    // Si la carga no ha comenzado o user?.id no está disponible, no hacer nada
    // Esto evita procesar el estado inicial antes de que la carga comience
    if (!hasStartedLoading || !user?.id) {
      return;
    }
    
    // Si estamos cargando y data es null/undefined, esperar a que lleguen los datos
    if (loadingTable && !data) {
      return;
    }
    
    // Si estamos cargando y data es un array vacío, verificar si la petición terminó
    // Solo procesar si la petición HTTP realmente terminó (isRequestComplete)
    if (loadingTable && Array.isArray(data) && data.length === 0 && hasStartedLoading && isRequestComplete) {
      // La carga terminó sin resultados
      setTableData([]);
      setLoadingTable(false);
      // Solo establecer loading=false si es la carga inicial (cuando loading es true)
      if (loading) {
        setLoading(false);
      }
      return;
    }
    
    // Si la petición no ha terminado, esperar
    if (loadingTable && Array.isArray(data) && data.length === 0 && !isRequestComplete) {
      return;
    }
    
    if (data && data.length > 0) {
      const parsedData = data?.map((element) => {
        return {
          correlative: {
            value: (
              <div className="custom-center-inner-td ml-3">
                <span>{element?.followingNumber}</span>
              </div>
            ),
            type: "correlative",
            id: element._id,
            comparative: element?.followingNumber,
          },
          sale: (
            <div className="custom-center-inner-td">
              {element?.senderUser?.senderBusinessName ||
                element?.senderInfo?.businessName ||
                t('Sin información')}
            </div>
          ),
          client: (
            <div className="d-grid">
              <span>{element?.receiver_name}</span>
              <span>{element?.receiver_phone}</span>
            </div>
          ),
          destiny: (
            <div className="d-grid">
              <span>
                {element?.shipping_address?.street_number != null
                  ? `${element?.shipping_address?.street_name}, ${element?.shipping_address?.street_number}`
                  : element?.shipping_address?.address_line}
              </span>
              <span className="custom-table-city-name">
                {element?.shipping_address?.municipality?.name}, {element?.shipping_address?.city?.name}
              </span>
              {/* Sin coordenada confirmada el paquete NO entra a la ruta
                  optimizada, así que la marca va junto a la dirección y no en
                  una columna de estado: es un problema de este dato.
                  Se comprueba el valor exacto y no la ausencia porque los
                  documentos anteriores al despliegue no tienen el bloque
                  `geocoding`, y no son pendientes: nunca se evaluaron. */}
              {element?.geocoding?.status === 'pending' && (
                <PendingAddressChip />
              )}
            </div>
          ),
          comment: (
            <div className="d-grid">
              <span>{element?.shipping_address?.comment || element?.comments || t('Sin comentarios')}</span>
            </div>
          ),
          deliveryMan: {
            name: element?.assignTo
              ? `${element?.assignTo?.name} ${element?.assignTo?.lastName}`
              : t('No asignado'),
            icon: element?.assignTo?.avatar,
          },
          business: element?.senderInfo?.businessName || t('Sin información'),
          lastUpdate: (
            <div className="custom-center-inner-td">
              {moment(element?.updatedAt).format("DD/MM/YY HH:mm")}
            </div>
          ),
          createdAt: (
            <div className="custom-center-inner-td">
              {moment(element?.createdAt).format("DD/MM/YY HH:mm")}
            </div>
          ),
          status: {
            statusPill: (
              <div className="custom-center-inner-td">
                <div className={`${packageStatusStyles[element?.status]}`}>
                  {packageStatus[element?.status]}
                </div>
              </div>
            ),
            value: element?.status,
            status: element?.status,
          },
          orderId: element?.order_id,
          packageType: element?.type,
          incidentId: element?.incidentId || null,
          incidentDate: element?.incidentDate || null,
          incidentResolvedAt: element?.incidentResolvedAt || null,
        };
      });
      setTableData(parsedData);
      setLoadingTable(false); // Establecer loadingTable en false después de procesar los datos
      // Solo establecer loading=false si es la carga inicial (cuando loading es true)
      if (loading) {
        setLoading(false);
      }
    } else if (data && Array.isArray(data) && data.length === 0 && hasStartedLoading && user?.id && isRequestComplete) {
      // Si data está vacío y la petición terminó, verificar si la carga terminó
      if (loadingTable) {
        // La carga terminó sin resultados
        setTableData([]);
        setLoadingTable(false);
        // Solo establecer loading=false si es la carga inicial (cuando loading es true)
        if (loading) {
          setLoading(false);
        }
      } else if (!loadingTable) {
        // loadingTable ya es false, solo asegurar que tableData esté vacío
        setTableData([]);
      }
    }
  }, [data, loadingTable, loading, hasStartedLoading, user?.id, isRequestComplete]);

  // "Entregados" cuenta por fecha de entrega y el resto por última modificación, así
  // que "Todos" es la suma de lo que muestran las cards, no un universo de pedidos.
  // `delivered` es null si el backend no pudo contarlo: no suma.
  const cardsTotal =
    pending + picked + inWarehouse + inRoute + (delivered ?? 0) + inStorage + pendingReturns;

  // Cards de estado = prefiltros (tono + ícono + mini barra), estilo mockup.
  // "Todos" limpia el filtro de estado. El resto filtra por status.
  const totalCards = [
    { title: t('Todos'), icon: "apps", tone: "brand", value: cardsTotal, isAll: true },
    { key: "pending", title: t('Pendientes'), icon: "schedule", tone: "neutral", value: pending, filter: { status: [0] } },
    { key: "picked", title: t('Recolectados'), icon: "package_2", tone: "info", value: picked, filter: { status: [1] } },
    { key: "warehouse", title: t('En bodega'), icon: "warehouse", tone: "info", value: inWarehouse, filter: { status: [2] } },
    { key: "route", title: t('En ruta'), icon: "local_shipping", tone: "warning", value: inRoute, filter: { status: [3, 30, 31, 32] } },
    { key: "delivered", title: t('Entregados'), icon: "check_circle", tone: "success", value: delivered, filter: { status: [4] } },
    { key: "failed", title: t('Entrega fallida'), icon: "cancel", tone: "error", value: inStorage, filter: { status: [6, 7] } },
    { key: "returns", title: t('Devoluciones'), icon: "assignment_return", tone: "reverse", value: pendingReturns, filter: { status: [21, 22] } },
  ];

  const handleCardClick = (clickedCard) => {
    // "Todos" o re-click de la card activa → limpiar filtro de estado.
    if (clickedCard.isAll || selectedCard?.key === clickedCard.key) {
      setSelectedCard(null);
      setFilters({});
      setCurrentPage(1);
      setCardParam("");
      setStatusParam([]);
      setTypeParam("");
      return;
    }
    setCurrentPage(1);
    setSelectedCard(clickedCard);
    setFilters({
      ...clickedCard.filter,
      dateStart: moment(showResume.startDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
      dateEnd: moment(showResume.endDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
    });
    // La card codifica el estado en ?card=; status/type quedan para los filtros
    // de columna (mutuamente excluyentes para no duplicar la fuente de verdad).
    setCardParam(clickedCard.key);
    setStatusParam([]);
    setTypeParam("");
  };

  // Cambio de rango desde el DateRangeFilter (preset rápido o personalizado).
  // Recibe fechas "YYYY-MM-DD"; actualiza el scope (showResume → recalcula cards)
  // y, si hay una card de estado activa, también el rango de los filtros.
  const handleRangeChange = (startYMD, endYMD, preset) => {
    setDateFilterCleared(false); // elegir un preset/rango re-activa el filtro
    // preset + from/to en UNA sola navegación para que no se pisen entre sí.
    // 'custom' ⇒ persistir ?from/?to; cualquier preset ⇒ limpiarlos (vuelven al
    // default "" ⇒ se omiten de la URL).
    setDateRange(
      preset === "custom"
        ? { preset, from: startYMD, to: endYMD }
        : { preset, from: "", to: "" }
    );
    setShowResume({
      startDate: moment(startYMD).format("MM/DD/YYYY"),
      endDate: moment(endYMD).format("MM/DD/YYYY"),
    });
    setUpdateCards(true);
    setCurrentPage(1);
    // Elegir un preset/rango reinicia cualquier ampliación incremental previa.
    setDateExpansions(0);
    setExpandedRange(null);
    expandBaseStartRef.current = null;
    if (selectedCard) {
      setFilters((prev) => ({
        ...prev,
        dateStart: startYMD,
        dateEnd: endYMD,
      }));
    }
  };

  // (Antes existía `clearDateFilter` para la "X" del rango → scope sin fecha.
  //  Se quitó: en Pedidos la fecha es obligatoria para no sobre-consultar la BD.
  //  `dateFilterCleared` queda siempre en false; para ir más atrás está "Ampliar
  //  búsqueda" (+90 días).)

  // (6) Ampliación incremental del estado vacío: cada click suma 30 días hacia
  // atrás al inicio del rango (mantiene el "hasta"/hoy) y re-ejecuta la
  // búsqueda. Acumulativo hasta MAX_DATE_EXPANSIONS; al llegar al tope el botón
  // se oculta (ver render). El ancla es expandBaseStartRef para que cada paso
  // sea base − (n × 30) días, sin arrastrar redondeos.
  const expandDateSearch = () => {
    if (dateExpansions >= MAX_DATE_EXPANSIONS) return;

    // Fijar el ancla en el primer click: el inicio actual del rango activo.
    if (expandBaseStartRef.current === null) {
      expandBaseStartRef.current = moment(showResume.startDate, "MM/DD/YYYY").startOf("day");
    }

    const nextExpansions = dateExpansions + 1;
    const newStart = expandBaseStartRef.current
      .clone()
      .subtract(nextExpansions * DATE_EXPANSION_DAYS, "days")
      .startOf("day");
    const newEnd = moment(showResume.endDate, "MM/DD/YYYY"); // mantiene el "hasta"

    setDateExpansions(nextExpansions);
    setDateFilterCleared(false); // seguimos con filtro de fecha (acotado), no histórico
    setExpandedRange({
      start: newStart.format("YYYY-MM-DD"),
      end: newEnd.format("YYYY-MM-DD"),
    });
    setShowResume({
      startDate: newStart.format("MM/DD/YYYY"),
      endDate: newEnd.format("MM/DD/YYYY"),
    });
    setUpdateCards(true);
    setCurrentPage(1);
    if (selectedCard) {
      setFilters((prev) => ({
        ...prev,
        dateStart: newStart.format("YYYY-MM-DD"),
        dateEnd: newEnd.format("YYYY-MM-DD"),
      }));
    }
  };

  // Cambiar de operador reinicia la paginación y fuerza el recálculo de las
  // KPI cards; el listado se re-dispara solo (logisticCompanyId está en las
  // dependencias de la carga).
  const handleOperadorChange = (e) => {
    setOperadorParam(e.target.value);
    setUpdateCards(true);
    setCurrentPage(1);
  };

  const headers = [
    t('Correlativo'),
    t('Vendedor'),
    t('Cliente'),
    t('Dirección de entrega'),
    t('Comentario'),
    t('Repartidor'),
    t('Última actualización'),
    t('Creado'),
    t('Estado'),
    " ",
  ];

  const [unappliedQuery, setUnappliedQuery] = useState("");

  const searchButtonAction = () => {
    // `unappliedQuery` starts as "" (string), so never read `.query` directly.
    const text = (unappliedQuery?.query ?? "").trim();
    // Mirrors the backend contract (filter-packages.dto: q 2..64 chars):
    // validating here avoids a request the server would reject with a 400.
    if (text.length === 1 || text.length > 64) {
      showToast(t('La búsqueda debe tener entre 2 y 64 caracteres.'), "warning");
      return;
    }
    setCurrentPage(1);
    if (text === "") {
      setQuery({ query: "", type: "" });
      setQParam("");
      return;
    }
    setQuery({ ...unappliedQuery, query: text });
    setQParam(text);
  };

  const searchValue = (query, queryType) => {
    setUnappliedQuery({ type: queryType, query: query });
  };

  if (loading) {
    // Skeleton inicial acorde al layout real (header + 8 KPI cards + tabla).
    return (
      <div className="w-100 all-orders-container mt-6" role="status" aria-live="polite">
        <span className="sr-only">{t('Cargando…')}</span>
        <div className="orders-header-container">
          <h2 className="orders-header-container__title">{t('Todos los pedidos')}</h2>
        </div>
        <SummaryCardsSkeleton count={totalCards.length} />
        <div className="all-orders-container__table">
          {/* +1 por la columna de checkbox que CustomTable antepone a los headers. */}
          <TableSkeleton rows={Math.min(docsPerPage || 8, 10)} columns={headers.length + 1} />
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-100 all-orders-container mt-6 orders-sticky"
      style={{ "--orders-cards-sticky-h": `${cardsStickyH}px` }}
    >
      {viewEvidence ? (
        <>
          <EvidenceView
            selectedPackage={packageEvidence}
            completePackages={data}
            setViewEvidence={setViewEvidence}
            // Editar o cancelar desde el detalle deja la lista vieja: se recarga
            // con el mismo disparador que usan los paneles del menú de cada fila.
            onPackageChanged={() => setUpdateData(true)}
          />
        </>
      ) : (
        <>
          {/* Header responsive con título y filtros temporales */}
          <div className="orders-header-container">
            <h2 className="orders-header-container__title">
              {t('Todos los pedidos')}
            </h2>
            <div className="summary-cards-header__date-range">
              {/* Selector de operador — solo el superadmin del sistema, que ve
                  pedidos de TODOS. Vacío = todos los operadores. */}
              {isPlatform && (
                <select
                  className="orders-operator-select"
                  value={operadorParam}
                  onChange={handleOperadorChange}
                  aria-label={t('Filtrar por operador logístico')}
                  disabled={loadingTable}
                >
                  <option value="">{t('Todos los operadores')}</option>
                  {operadores.map((op) => (
                    <option key={op._id} value={op._id}>
                      {op.name}
                    </option>
                  ))}
                </select>
              )}
              {/* Sin `onClear` a propósito: en Pedidos la fecha NO se puede dejar
                  vacía (un scope sin fecha trae demasiada data y sobre-consume la
                  BD). Siempre hay un rango acotado; para ir más atrás está
                  "Ampliar búsqueda" (+90 días). */}
              <DateRangeFilter
                value={expandedRange ? "custom" : datePreset}
                onChange={handleRangeChange}
                customRange={
                  expandedRange ||
                  (datePreset === "custom" && fromParam && toParam
                    ? { start: fromParam, end: toParam }
                    : null)
                }
              />
            </div>
          </div>
          
          {/* Cards de resumen horizontales compactas */}
          <div className="summary-cards-container" ref={cardsRef}>
            {totalCards.map((item, index) => {
              const active = item.isAll
                ? !selectedCard
                : selectedCard?.key === item.key;
              const pct = cardsTotal > 0 ? Math.round((item.value / cardsTotal) * 100) : 0;
              return (
                <button
                  key={index}
                  type="button"
                  className={`summary-card summary-card--${item.tone}${
                    active ? " summary-card--active" : ""
                  }`}
                  // La tarjeta sigue respondiendo durante la carga de la tabla: con
                  // `pointer-events` apagado el clic no hacía nada y Clarity lo
                  // contaba como clic muerto (la mayoría de los usuarios de escritorio
                  // busca y hace clic en una tarjeta mientras la tabla recarga).
                  onClick={() => handleCardClick(item)}
                >
                  <div className="summary-card__top">
                    <span className="summary-card__icon" aria-hidden="true">
                      <span className="material-symbols-outlined">{item.icon}</span>
                    </span>
                    <span className="summary-card__value">
                      {item.value == null ? "—" : item.value.toLocaleString()}
                    </span>
                  </div>
                  <div className="summary-card__label">{item.title}</div>
                  {!item.isAll && (
                    <div className="summary-card__bar">
                      <div
                        className="summary-card__bar-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <div className="all-orders-container__table" id="table-container">
            <CustomTable
              title={""}
              headers={headers}
              data={tableData}
              totalDocs={totalDocs}
              page={null}
              actions={true}
              completeData={data}
              updateData={setUpdateData}
              docsPerPage={docsPerPage}
              loadingTable={loadingTable}
              pageSelected={null}
              setViewEvidence={setViewEvidence}
              setPackageEvidence={setPackageEvidence}
              filters={filters}
              setFilters={(value) => {
                setSelectedCard(null);
                setFilters(value);
                // Filtro de columna → status/type a la URL; se limpia ?card=.
                setCardParam("");
                setStatusParam(
                  Array.isArray(value?.status) ? value.status.map(String) : []
                );
                setTypeParam(value?.type || "");
              }}
              admin={true}
              infiniteScroll={true}
              showSearchBar={!viewEvidence}
              searchBarProps={!viewEvidence ? {
                onChange: searchValue,
                loading: loadingTable,
                searchButton: true,
                onSearchButton: searchButtonAction,
                consolidated: true,
                value: query.query
              } : null}
              emptyState={
                loadError ? (
                  <ErrorState message={loadError} onRetry={() => loadData(1, true)} />
                ) : undefined
              }
            />
          </div>

          {/* Elemento observador para scroll infinito - fuera del contenedor de la tabla */}
          {data.length > 0 && !loadingTable && (
            <>
              {hasMore && (
                <div ref={observerTarget} className="infinite-scroll-observer">
                  {loadingMore && (
                    <div className="infinite-scroll-loading">
                      <Spinner />
                      <span className="ml-2">{t('Cargando más pedidos...')}</span>
                    </div>
                  )}
                </div>
              )}
              {!hasMore && (
                <div className="infinite-scroll-end">
                  <span>{t('No hay más pedidos para mostrar')}</span>
                </div>
              )}
            </>
          )}

          {/* (6) Sin resultados + filtro de fecha activo → ampliar la búsqueda
              de forma incremental (+90 días por click) hasta un tope razonable
              (MAX_DATE_EXPANSIONS ≈ 540 días). Indica hasta qué fecha se está
              buscando y, al llegar al tope, oculta el botón y avisa.
              Estilos inline con CSS vars del tema (resume.jsx no tiene scss
              propio; combina solo y se adapta a dark mode). */}
          {!loadingTable &&
            isRequestComplete &&
            data.length === 0 &&
            !dateFilterCleared &&
            !loadError && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "0.625rem",
                  padding: "0.5rem 1rem 2rem",
                  textAlign: "center",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8125rem",
                    color: "var(--text-tertiary)",
                  }}
                >
                  {dateExpansions > 0
                    ? t('Buscando desde el {{fecha}}', {
                        fecha: moment(showResume.startDate, "MM/DD/YYYY")
                          .locale("es")
                          .format("D MMM YYYY"),
                      })
                    : t('¿Buscas un pedido antiguo? Está fuera del rango de fechas actual.')}
                </p>
                {dateExpansions >= MAX_DATE_EXPANSIONS ? (
                  <p
                    style={{
                      margin: 0,
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      color: "var(--text-secondary)",
                    }}
                  >
                    {t('No se encontraron pedidos en el rango máximo de búsqueda.')}
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={expandDateSearch}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.375rem",
                      height: "2.25rem",
                      padding: "0 0.875rem",
                      border: "1px solid var(--color-primary)",
                      borderRadius: "var(--radius-md)",
                      backgroundColor: "transparent",
                      color: "var(--color-primary)",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: "1.125rem" }}>
                      manage_search
                    </span>
                    {t('Ampliar búsqueda 30 días más')}
                  </button>
                )}
              </div>
            )}
        </>
      )}
    </div>
  );
}